import { useEffect, useState } from "react";

import { css } from "@emotion/react";
import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  DESTRUCTIVE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Grid,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { Dropzone, FileWithPath, MIME_TYPES } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons";
import { FileRejection } from "react-dropzone";

import { ReactComponent as IconPhoto } from "assets/icons/photo.svg";

import {
  SupportedMediaMime,
  SupportedMediaType,
} from "../../apollo/graphql.generated";

import { useGetUploadUrlForContractUriMutation } from "./queries/profile.graphql.generated";

export interface Props {
  onMediaChanged: (
    media: {
      url: string;
      type: string;
    } | null
  ) => void;
  mediaError: string;
  mediaUrl?: string;
  mediaType: SupportedMediaType;
  edited?: boolean;
}

const mimeFromType: Record<string, string> = {
  "image/png": SupportedMediaMime.PNG,
  "image/webp": SupportedMediaMime.WEBP,
  "image/gif": SupportedMediaMime.GIF,
  "image/jpeg": SupportedMediaMime.JPEG,
  "image/jpg": SupportedMediaMime.JPEG,
};

const uploadToS3 = async (file: File, url: string) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error(
      `[ImageUploader.uploadToS3] error response from s3: ${JSON.stringify(
        response
      )}`
    );
  }
};

export const UPLOAD_ERROR =
  "Could not upload media! Refresh and try again, or contact support!";

const ImageUploader = ({
  mediaError: errorFromProps,
  onMediaChanged,
  mediaUrl,
  edited = false,
}: Props) => {
  const [media, setMedia] = useState<FileWithPath | null>(null);
  const [localMediaUrl, setLocalMediaUrl] = useState<string | undefined>(
    mediaUrl
  );
  const [mediaError, setMediaError] = useState<string>(errorFromProps);
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);
  const [getUploadUrl] = useGetUploadUrlForContractUriMutation();
  const [rejections, setRejections] = useState<FileRejection[]>([]);

  useEffect(() => {
    setMediaError(errorFromProps);
  }, [errorFromProps]);

  const onFileSelected = (files: FileWithPath[]) => {
    const file = files[0];
    const maxSizeInMb = 25;
    if (file.size > maxSizeInMb * 1000000) {
      const error = `The file size cannot be greater than ${maxSizeInMb}MB.`;
      setMediaError(error);
    } else {
      setMedia(file);
      setMediaError("");
    }
  };

  const clearAllAttachments = () => {
    setMedia(null);
    setMediaError("");
    onMediaChanged(null);
  };

  const getMediaUrls = async (mime: SupportedMediaMime) => {
    const getUrlResponse = await getUploadUrl({
      variables: { data: { mime } },
    });

    const metadata = getUrlResponse?.data?.getUploadUrlForContractUri?.metadata;
    if (!metadata) {
      throw new Error(
        `[ImageUploader] Unexpected response from getUploadUrlForContractUri: ${JSON.stringify(
          { getUrlResponse }
        )}`
      );
    }

    const { url, publicUrl } = metadata;
    if (!url || !publicUrl) {
      throw new Error(
        `[ImageUploader] Unexpected upload media metadata: ${JSON.stringify({
          metadata,
        })}`
      );
    }

    return { url, publicUrl };
  };

  /**
   * Upload media files and replaces the local blob URLs with the public CDN ones.
   * @param file file to be uploaded
   */
  const uploadMediaFileToS3 = async (file: File) => {
    try {
      setIsMediaUploading(true);
      const { url, publicUrl } = await getMediaUrls(
        mimeFromType[file.type] as SupportedMediaMime
      );
      await uploadToS3(file, url);
      onMediaChanged({ url: publicUrl, type: file.type });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      logError({
        err,
        message: "[ImageUploader] Failed to fetch upload URL",
      });

      setMediaError(UPLOAD_ERROR);
      setMedia(null);
      onMediaChanged(null);
    } finally {
      setIsMediaUploading(false);
    }
  };

  useEffect(() => {
    if (media) {
      setLocalMediaUrl(URL.createObjectURL(media));
      uploadMediaFileToS3(media); // runs async
    }
  }, [media]);

  const mediaPreview = (mediaUrl || media) &&
    localMediaUrl &&
    !isMediaUploading && (
      <ResizedImage
        src={localMediaUrl}
        height={48}
        width={48}
        radius={999}
        fit="fill"
        sx={(theme) => ({
          border: `0.5px solid ${theme.colors.divider[0]}`,
          borderRadius: 999,
        })}
        imageProps={{ onLoad: () => URL.revokeObjectURL(localMediaUrl) }}
      />
    );
  const supportedTypes = [
    MIME_TYPES.jpeg,
    MIME_TYPES.png,
    MIME_TYPES.webp,
    MIME_TYPES.gif,
  ];

  const mediaUpload = (!media || isMediaUploading) && (
    <Box p={0}>
      <Group>
        <Dropzone
          multiple={false}
          accept={supportedTypes}
          onDrop={async (files) => {
            setRejections([]);
            onFileSelected(files);
          }}
          onReject={(files) => {
            setRejections(files);
          }}
          loading={isMediaUploading}
          sx={(theme) => ({
            borderWidth: "1px",
            borderColor: mediaError
              ? theme.colors.errorStatus[0]
              : theme.colors.divider[0],
            borderStyle: "solid",
            minWidth: "64px",
            minHeight: "64px",
            maxWidth: "64px",
            maxHeight: "64px",
          })}
          p={0}
        >
          <AspectRatio ratio={1}>
            <Stack
              align="center"
              justify="center"
              spacing={0}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <IconUpload size={24} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={24} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  css={css`
                    width: 24px;
                    height: 24px;
                  `}
                />
              </Dropzone.Idle>
            </Stack>
          </AspectRatio>
        </Dropzone>

        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          Upload an image...
        </Text>
      </Group>
      {rejections.map((rejection: FileRejection) => (
        <>
          <Text
            key={rejection.file.name + rejection.file.lastModified}
            mt="sm"
            size="sm"
            color={STATUS_COLOR.ERROR}
          >
            Error for {rejection.file.name}:
          </Text>
          {rejection.errors.map((error) => (
            <Text
              key={error.code}
              mt="sm"
              size="sm"
              color={STATUS_COLOR.ERROR}
              sx={() => ({ overflowWrap: "break-word" })}
            >
              {error.message}
            </Text>
          ))}
        </>
      ))}
    </Box>
  );

  return (
    <Stack spacing={4}>
      <BoxContainer
        sx={(theme) => ({
          borderWidth: "1px",
          borderColor: mediaError
            ? theme.colors.errorStatus[0]
            : theme.colors.divider[0],
          borderStyle: "solid",
        })}
      >
        <Grid align="center" justify="space-between">
          <Grid.Col span={6}>
            {!mediaPreview && mediaUpload}
            {mediaPreview}
          </Grid.Col>
          <Grid.Col span={4} offset={2}>
            <Center sx={{ justifyContent: "right" }}>
              {edited && mediaUrl && (
                <Button
                  size="xs"
                  type={"submit"}
                  color={DESTRUCTIVE_COLOR}
                  px={12}
                  onClick={clearAllAttachments}
                >
                  Remove
                </Button>
              )}
              {!edited && mediaUrl && (
                <Button
                  size={"lg"}
                  type={"submit"}
                  color={SECONDARY_COLOR}
                  px={12}
                  onClick={clearAllAttachments}
                >
                  Upload custom logo
                </Button>
              )}
            </Center>
          </Grid.Col>
        </Grid>
      </BoxContainer>
      {mediaError && (
        <Text size="xs" color={STATUS_COLOR.ERROR}>
          {mediaError}
        </Text>
      )}
    </Stack>
  );
};

export default ImageUploader;
